import { Link, createFileRoute } from "@tanstack/react-router";

import { useForceLightMode } from "@/hooks/useTheme";
import { MarketingHeader } from "@/components/layout/MarketingHeader";

export const Route = createFileRoute("/privacy")({
  component: PrivacyRoute,
});

function PrivacyRoute() {
  useForceLightMode();
  return (
    <div className="relative flex min-h-screen flex-col bg-surface-light pt-20 text-black transition-colors dark:bg-surface-dark dark:text-white">
      <MarketingHeader />

      <article className="mx-auto w-full max-w-3xl px-6 py-16">
        {/* Header */}
        <header className="mb-12 border-b border-black/10 pb-8 dark:border-white/10">
          <h1 className="mb-3 text-4xl font-heading md:text-5xl">Privacy Policy</h1>
          <p className="text-lg text-black/60 dark:text-white/60">
            Soulbound Labs Inc. — Clawcraft™ Platform
          </p>
          <p className="mt-1 text-sm text-black/40 dark:text-white/40">
            Last Updated: May 6, 2026
          </p>
        </header>

        {/* Intro */}
        <div className="space-y-4 text-[15px] leading-relaxed text-black/80 dark:text-white/80">
          <p>
            This Privacy Policy ("Policy") describes how Soulbound Labs Inc. ("Soulbound Labs," "we," "us," or "our") collects, uses, shares, and protects information when you use the Clawcraft™ platform, website (<a href="https://clawcraft.ca" className="text-brand-coral-dark underline decoration-brand-coral-dark/30 hover:decoration-brand-coral-dark dark:text-brand-coral dark:decoration-brand-coral/30 dark:hover:decoration-brand-coral">clawcraft.ca</a>), and related services (collectively, the "Services"). Clawcraft is a product owned and operated by Soulbound Labs Inc., a corporation incorporated under the laws of Canada with its registered office in Montreal, Quebec. "Personal Information" means any information relating to an identified or identifiable individual.
          </p>
          <p>
            By accessing or using the Services, you consent to the practices described in this Policy. If you do not agree, please do not use the Services.
          </p>

          {/* Callouts */}
          <div className="my-6 rounded-lg border border-brand-coral-dark/30 bg-brand-coral-dark/5 p-4 text-sm dark:border-brand-coral/30 dark:bg-brand-coral/5">
            <strong>IMPORTANT — AI AGENTS:</strong> The Services allow you to connect third-party applications (email, calendars, productivity tools, etc.) to AI-powered agents. When you establish such connections, AI agents will access and process data from those services on your behalf, which may include personal data belonging to you and others. You are responsible for ensuring that your use complies with applicable privacy laws and that you have obtained any required consents.
          </div>

          <div className="my-6 rounded-lg border border-black/10 bg-black/[0.02] p-4 text-sm dark:border-white/10 dark:bg-white/[0.02]">
            <strong>WHITE-LABEL NOTICE:</strong> If you access the Services through a partner or agency (a "Channel Partner"), the Channel Partner may have its own privacy policy governing how it handles your data. Clawcraft provides the underlying infrastructure and processes data on behalf of the Channel Partner as a service provider / data processor. Please review your Channel Partner's privacy policy for information about their specific practices.
          </div>
        </div>

        {/* Sections */}
        <div className="mt-12 space-y-10 text-[15px] leading-relaxed text-black/80 dark:text-white/80 [&_h2]:mb-4 [&_h2]:mt-0 [&_h2]:text-2xl [&_h2]:font-heading [&_h2]:text-black [&_h2]:dark:text-white [&_h3]:mb-3 [&_h3]:mt-6 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-black [&_h3]:dark:text-white [&_ul]:my-3 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-6 [&_p+p]:mt-3">

          <section>
            <h2>1. Information We Collect</h2>
            <h3>Information You Provide</h3>
            <ul>
              <li><strong>Contact Information:</strong> Name, email address, phone number, company name, and job title.</li>
              <li><strong>Account Data:</strong> Username, password, and profile information you create when registering.</li>
              <li><strong>Payment Information:</strong> Billing details provided when subscribing. Payments are processed by a third-party processor; we do not store complete payment card numbers.</li>
              <li><strong>Communications:</strong> Information you provide when contacting support or corresponding with us, including the content and metadata of those messages.</li>
              <li><strong>Connected Service Credentials:</strong> Authentication credentials for third-party services you connect to the platform (e.g., Google, Telegram). These are stored securely and used solely to maintain your authorized integrations.</li>
            </ul>

            <h3>Information from Connected Services</h3>
            <p>When you authorize connections to third-party services, AI agents operating within your environment may access and process data from those services, including:</p>
            <ul>
              <li>Emails and message content</li>
              <li>Contacts and address books</li>
              <li>Calendar events and scheduling data</li>
              <li>Files, documents, and spreadsheets</li>
              <li>Other data accessible through connected APIs</li>
            </ul>
            <p><em className="text-black/60 dark:text-white/60">Note:</em> Clawcraft's role is limited to enabling the connection and providing the runtime infrastructure. AI agents access and process connected-service data based on your instructions. You maintain control over and responsibility for all data accessed from connected services.</p>

            <h3>Information Collected Automatically</h3>
            <ul>
              <li><strong>Device and Log Data:</strong> IP address, browser type and version, operating system, device identifiers, access times, and referring URLs.</li>
              <li><strong>Usage Data:</strong> Pages visited, features used, and interaction patterns within the Services.</li>
              <li><strong>Cookies and Similar Technologies:</strong> See Section 5.</li>
            </ul>
          </section>

          <section>
            <h2>2. How We Use Personal Information</h2>
            <p>We use Personal Information for the following purposes:</p>
            <ul>
              <li><strong>Providing the Services:</strong> Delivering, operating, and personalizing the platform; processing payments; provisioning agent environments; managing accounts; and enabling connections to third-party services.</li>
              <li><strong>Support:</strong> Diagnosing and resolving technical issues.</li>
              <li><strong>Communications:</strong> Sending administrative notices, service updates, security alerts, and responding to your inquiries.</li>
              <li><strong>Improvement:</strong> Analyzing usage trends and preferences to enhance the Services and develop new features.</li>
              <li><strong>Security:</strong> Detecting, preventing, and addressing fraud, abuse, and security incidents.</li>
              <li><strong>Legal and Compliance:</strong> Complying with applicable laws, regulations, and legal processes.</li>
            </ul>

            <div className="my-6 rounded-lg border border-brand-coral-dark/30 bg-brand-coral-dark/5 p-4 text-sm dark:border-brand-coral/30 dark:bg-brand-coral/5">
              <strong>We do not use Customer Content</strong> (including data from connected services) <strong>to train AI models</strong> or for any purpose beyond providing the Services to you.
            </div>
          </section>

          <section>
            <h2>3. When We Share Information</h2>
            <p>We may share information in the following circumstances:</p>
            <ul>
              <li><strong>Service Providers:</strong> Third-party vendors who help us operate the Services (hosting, analytics, payment processing). They are contractually required to protect your information.</li>
              <li><strong>AI Providers:</strong> When you use AI agents, your prompts and instructions may be processed by third-party AI providers (e.g., Anthropic, OpenAI) through Soulbound Labs' managed infrastructure.</li>
              <li><strong>Connected Services:</strong> Information is shared with third-party services you connect, governed by those services' own terms and privacy policies.</li>
              <li><strong>Channel Partners:</strong> If you access the Services through a Channel Partner, we may share account and usage information with that partner as necessary to deliver and support the Services.</li>
              <li><strong>Legal Requirements:</strong> When required by law, regulation, court order, or governmental request.</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets.</li>
              <li><strong>With Your Consent:</strong> When you direct or authorize us to share information.</li>
            </ul>
          </section>

          <section>
            <h2>4. AI Agents and Data Processing</h2>
            <p>The Services enable AI agents to perform automated tasks within an isolated environment on your behalf. Please note:</p>
            <ul>
              <li><strong>Data Access:</strong> AI agents may access, read, process, modify, send, and delete data from connected services based on your instructions. This may include personal data of third parties (e.g., email correspondents, contacts).</li>
              <li><strong>AI Provider Processing:</strong> Data processed by AI agents may be transmitted to third-party AI providers through Soulbound Labs' managed infrastructure. Such transmission is subject to the AI provider's terms of service.</li>
              <li><strong>Logging:</strong> Activity within your environment may be logged for troubleshooting and security purposes.</li>
              <li><strong>Infrastructure Location:</strong> Our primary infrastructure is hosted in the Montreal region. AI provider calls and certain other processing may occur outside Canada depending on the providers used.</li>
            </ul>
            <p><strong>Your Responsibilities:</strong> You are solely responsible for ensuring you have the authority to connect services and grant AI agents access; obtaining any required consents from individuals whose data may be processed; and complying with applicable privacy laws.</p>
          </section>

          <section>
            <h2>5. Cookies and Tracking Technologies</h2>
            <p>We use cookies and similar technologies for:</p>
            <ul>
              <li><strong>Essential Cookies:</strong> Required for authentication, security, and core platform functionality.</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how visitors use the Services.</li>
              <li><strong>Preference Cookies:</strong> Store your settings and preferences.</li>
            </ul>
            <p>You can manage cookie preferences through your browser settings. Disabling certain cookies may affect functionality.</p>
          </section>

          <section>
            <h2>6. Your Rights and Choices</h2>
            <p>Depending on your location, you may have the following rights:</p>
            <ul>
              <li><strong>Access:</strong> Request a copy of the Personal Information we hold about you.</li>
              <li><strong>Deletion:</strong> Request deletion of your Personal Information, subject to certain exceptions.</li>
              <li><strong>Correction:</strong> Request correction of inaccurate Personal Information.</li>
              <li><strong>Opt-Out:</strong> Opt out of targeted advertising or the sale/sharing of your Personal Information.</li>
              <li><strong>Portability:</strong> Receive your Personal Information in a structured, machine-readable format.</li>
              <li><strong>Withdraw Consent:</strong> Withdraw consent where processing is based on consent.</li>
            </ul>
            <p>To exercise these rights, contact us at <a href="mailto:support@clawcraft.ca" className="text-brand-coral-dark underline decoration-brand-coral-dark/30 hover:decoration-brand-coral-dark dark:text-brand-coral dark:decoration-brand-coral/30 dark:hover:decoration-brand-coral">support@clawcraft.ca</a>. We will respond within the timeframes required by applicable law.</p>
            <p><strong>Marketing Communications:</strong> You may opt out of promotional emails by following the unsubscribe instructions in any marketing message or by contacting us.</p>
          </section>

          <section>
            <h2>7. Data Retention</h2>
            <p>We retain Personal Information only as long as necessary to fulfill the purposes described in this Policy, unless a longer retention period is required by law.</p>
            <ul>
              <li><strong>Account Information:</strong> Retained for the duration of your account and a reasonable period afterward for legal and audit purposes.</li>
              <li><strong>Agent Environment Data:</strong> Customer content within your environment is retained while your account is active. Following account termination, data may be deleted after thirty (30) days.</li>
              <li><strong>Usage and Log Data:</strong> Retained for up to 90 days for analytics and security purposes.</li>
            </ul>
          </section>

          <section>
            <h2>8. Information Security</h2>
            <p>We implement reasonable security measures, including:</p>
            <ul>
              <li>Encryption in transit (TLS) and at rest for sensitive data.</li>
              <li>Per-tenant environment isolation to prevent unauthorized cross-access.</li>
              <li>Access controls restricting information access to authorized personnel.</li>
              <li>Security monitoring and vulnerability management.</li>
            </ul>
            <p className="italic text-black/60 dark:text-white/60">No security system is impenetrable. You are responsible for maintaining the security of your account credentials and access to your environment.</p>
          </section>

          <section>
            <h2>9. International Data Transfers</h2>
            <p>Our primary infrastructure is hosted in Canada. If you access the Services from outside Canada, your Personal Information may be transferred to and processed in Canada. For individuals in the EU/EEA, UK, or Switzerland, we rely on appropriate transfer mechanisms such as Standard Contractual Clauses where required.</p>
          </section>

          <section>
            <h2>10. Children's Privacy</h2>
            <p>The Services are not intended for individuals under the age of 18. We do not knowingly collect Personal Information from children. If you believe we have collected information from a child, please contact us and we will take steps to remove it.</p>
          </section>

          <section>
            <h2>11. Third-Party Services</h2>
            <p>The Services may contain links to or integrations with third-party services. We are not responsible for the privacy practices of those services. We encourage you to review their privacy policies before providing them with any information.</p>
          </section>

          <section>
            <h2>12. Changes to This Policy</h2>
            <p>We may update this Policy from time to time. The "Last Updated" date at the top indicates the most recent revision. For material changes, we will provide notice through the Services or via email. Continued use of the Services after changes take effect constitutes acceptance of the updated Policy.</p>
          </section>

          <section>
            <h2>13. How to Contact Us</h2>
            <div className="rounded-lg border border-black/10 bg-black/[0.02] p-5 dark:border-white/10 dark:bg-white/[0.02]">
              <strong className="text-black dark:text-white">Soulbound Labs Inc.</strong><br />
              Montreal, Quebec, Canada<br />
              <strong>Email:</strong>{" "}
              <a href="mailto:support@clawcraft.ca" className="text-brand-coral-dark underline decoration-brand-coral-dark/30 hover:decoration-brand-coral-dark dark:text-brand-coral dark:decoration-brand-coral/30 dark:hover:decoration-brand-coral">support@clawcraft.ca</a><br />
              <strong>Website:</strong>{" "}
              <a href="https://clawcraft.ca" className="text-brand-coral-dark underline decoration-brand-coral-dark/30 hover:decoration-brand-coral-dark dark:text-brand-coral dark:decoration-brand-coral/30 dark:hover:decoration-brand-coral">clawcraft.ca</a>
            </div>
          </section>
        </div>
      </article>

      {/* Minimal footer */}
      <footer className="border-t border-black/10 px-6 py-8 text-center text-sm text-black/40 dark:border-white/10 dark:text-white/40">
        <Link to="/" className="transition-colors hover:text-brand-coral-dark dark:hover:text-brand-coral">
          Back to Clawcraft
        </Link>
        <span className="mx-3">|</span>
        <Link to="/terms" className="transition-colors hover:text-brand-coral-dark dark:hover:text-brand-coral">
          Terms of Service
        </Link>
      </footer>
    </div>
  );
}
